import React, { useState } from 'react';
import { injectIntl, FormattedMessage } from '@kyruus/intl';
import { withRouter } from 'react-router-dom';
import _get from 'lodash/get';
import { useDispatch } from 'react-redux';
import { fetchWaitTimeByLocationID } from '../../behaviors/search/actions';
import {
  ProviderTileClassic,
  ProviderTileModern,
  PLACEMENT_TYPE_SEARCH
} from '@kyruus/provider-tile-next';
import {
  ProviderReferralButton,
  ProviderReferralList
} from '@kyruus/provider-referral';
import {
  MODULES,
  isModuleEnabled,
  shouldRenderDirectBookInDrawer,
  isStaffPersona
} from 'Common/config';
import { shouldOpenDirectBookSameWindow } from 'Src/utils/search-common';
import { getConfigProperty, LIST_VIEW_MODE } from 'Common/config';
import {
  getProviderUrl,
  getProviderSummaryUrl,
  getProviderDisplayName,
  getProviderPhoneNumber
} from '../../provider/utils';
import { pages } from '../../tracking/constants';
import { pageViewEvent } from '../../tracking/tracking-utils';
import { getLogWithProvider } from '../../tracking/decorators';
import { getRelativeParameterizedBookingUrl } from '../../utils/getRelativeParameterizedBookingUrl';
import { locationsModuleEnabled, buildLocationURL } from '../../utils/location';

import { MAX_AVAILABILITIES_TO_DISPLAY } from '../../utils/constants';
import { isCCF, scrollToTopCCF } from '../../utils/cerner';
import { useReferralCrmConnector } from '../../referral-crm-connector';
import { messages as crmMessages } from 'Src/referral-crm-connector/useCrmConnector/messages';
import { messages } from './messages';
import {
  getProviderSlotsAndAppointmentInfo,
  getProviderSlotsStatus
} from './utils';

import { AvailabilityDrawer } from './AvailabilityDrawer';
import { CtaDrawer, getCtaListItemsToRender } from 'Src/cta-drawer';
import {
  AVAILABILITY_DRAWER,
  BOOKING_MODAL,
  TILE_CLICK_ORIGIN,
  TRACKING_EVENTS
} from './constants';
import { BookingModal } from './BookingModal';
import { useAppDrawer, APP_DRAWER_IDS } from 'Src/app-drawer';
import { ProviderTileWrapper } from './ProviderTileWrapper';
import { ProviderCardListContainer, ProviderCardList } from './styles';

function ProviderList({
  appointmentInfo,
  apptOptions, // appointment options to be passed as deepLinkingParam
  availabilityLinkTarget,
  bookingCallbackFunc,
  config,
  customerCode,
  history,
  intl,
  location,
  log,
  match,
  onAvailabilityTilesLoaded,
  onClickShowAvailability,
  onTimeSlotsClick,
  onViewMoreClick, // callback when clicking view more availability tiles button
  productName,
  profileSummaryParams = {}, // extra params to send to send along to provider profile pages
  providerCallbackFunc, // provider title and ratings callback function
  providers = [],
  searchSummary,
  slots, // slots by provider id
  slotsStatus, // status by provider id,
  fetchProviderAvailability,
  fetchPurposesByProviderId,
  purposeOptionsByProviderId,
  availabilityError,
  onProviderHover,
  onKeyboardFocus,
  onKeyboardBlur,
  purposesLoading,
  isProviderListMapsEnabled = false,
  showProviderLocationsInProviderTile = true
}) {
  const [selectedPatientRelationship, setSelectedPatientRelationship] =
    useState('new');
  const [showProviderModal, setShowProviderModal] = useState(undefined);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const showAvailabilityModal =
    showProviderModal?.provider && showProviderModal?.modal === 'availability';
  const showBookingModal =
    showProviderModal?.provider && showProviderModal?.modal === 'booking';
  const dispatch = useDispatch();
  const { openAppDrawer, closeAppDrawer, activeAppDrawer } = useAppDrawer();

  const isLocationsModuleEnabled = locationsModuleEnabled(config);

  const { enabled: crmEnabled, connector } = useReferralCrmConnector(config);

  const isCtaDrawerEnabled = _get(
    config,
    'darkship_feature_flags.use_cta_drawer',
    false
  );
  // Get modal display
  const virtualCareOrg = _get(config, 'modal_display.virtual_care_org');
  const skipModal = _get(config, 'modal_display.skip_modal');

  const bookingCallbackFunction = (provider) => {
    return (_e) => {
      if (bookingCallbackFunc) {
        bookingCallbackFunc(provider);
      }
      const providerBookUrl = provider.book_online_url;
      const providerVirtualCareUrl = provider.virtual_care_url;
      const virtualCareEnabled = providerVirtualCareUrl && virtualCareOrg;
      // the boolean skipModal comes from an org's config and indicates that the call to action button should bypass the modal
      // however, this feature is only active if the org does not have both direct booking and virtual care configured
      // in the case that skipModal is turned on and direct booking and virtual care are active,
      // we render the modal so the user can select which option they prefer.
      // Agent and employed user personas do not use the CTA modal.
      const canSkipModal =
        isStaffPersona(config) ||
        (skipModal &&
          (providerBookUrl || virtualCareEnabled) &&
          !(providerBookUrl && virtualCareEnabled));
      if (canSkipModal) {
        const url =
          getRelativeParameterizedBookingUrl({
            provider,
            config,
            location,
            searchSummary
          }) || provider.virtual_care_url;

        if (shouldRenderDirectBookInDrawer(config)) {
          // Push the current URL onto the history stack
          // along with the selected provider data as part of the location state
          // as required for react-router v4 modal routing.
          // See https://v5.reactrouter.com/web/example/modal-gallery for more info.
          history.push(url, {
            provider: {
              location,
              provider
            }
          });
        } else if (shouldOpenDirectBookSameWindow()) {
          window.open(url, '_self');
        } else {
          const win = window.open(url, '_blank');
          if (win != null) {
            win.focus();
          }
        }
      } else {
        scrollToTopCCF();
        // treat showing the modal as a "page event"
        getLogWithProvider(log, provider)(pageViewEvent(pages.CTA_MODAL));
        setShowProviderModal({ provider, modal: 'booking' });
        // Allow drawer to mount before opening so animation will show
        setTimeout(() => {
          openAppDrawer(APP_DRAWER_IDS.CTA_DRAWER);
        }, 1);
      }
    };
  };

  const providerCallbackFunction = (provider, hash = '', slots) => {
    return (e) => {
      if (providerCallbackFunc) {
        providerCallbackFunc(provider);
      }
      const url = getProviderSummaryUrl(
        provider,
        hash,
        slots,
        profileSummaryParams,
        location,
        match
      );
      if (e.ctrlKey || e.metaKey) {
        window.open(url);
      } else {
        // force a server side render so that the redux context is reset with the search v9 provider (vs the v8 provider)
        window.open(url, '_self');
        // TODO: once search v9 migration is complete on the searchmatch page, switch to the more performant react-router history:
        // history.push(url);
      }
    };
  };

  const locationCallbackFunction = (locationUrl, isExternalUrl = false) => {
    log('user_action.display_location_profile_page');

    if (isExternalUrl && typeof window !== 'undefined') {
      window.open(locationUrl, '_blank', 'noopener');
    }
    if (!isExternalUrl) {
      history.push(locationUrl);
    }
  };

  const getWaitTimeCallbackFunction = (locationEhrId, sourceSystem) => {
    dispatch(fetchWaitTimeByLocationID(locationEhrId, sourceSystem));
  };

  const handleTimeSlotsClick = (event, origin, data) => {
    const { date, time, unformattedDate, patient_relationship, provider } =
      data;
    const isHeadsUpAvailabilityEnabled = _get(
      config,
      'darkship_feature_flags.provider_tile_heads_up_availability',
      false
    );
    if (
      origin === TILE_CLICK_ORIGIN.PROVIDER_TILE &&
      isHeadsUpAvailabilityEnabled
    ) {
      setSelectedPatientRelationship(patient_relationship);
      setShowProviderModal({ provider, modal: 'availability' });
      setTimeout(() => {
        openAppDrawer(APP_DRAWER_IDS.AVAILABILITY_DRAWER);
      }, 1);
      setSelectedDate(unformattedDate);
      getLogWithProvider(log, provider)(TRACKING_EVENTS.TIMESLOT_CLICK, {
        date,
        time
      });
    } else if (origin === TILE_CLICK_ORIGIN.AVAILABILITY_DRAWER) {
      onTimeSlotsClick(event, data);
    }
  };

  const handleViewMoreClick = (event, clickOrigin, data) => {
    const { viewMoreBookOnlineUrl } = data;
    onViewMoreClick(event, data);

    const isHeadsUpAvailabilityEnabled = _get(
      config,
      'darkship_feature_flags.provider_tile_heads_up_availability',
      false
    );

    if (
      isHeadsUpAvailabilityEnabled &&
      clickOrigin === TILE_CLICK_ORIGIN.PROVIDER_TILE &&
      !shouldRenderDirectBookInDrawer(config)
    ) {
      const target = shouldOpenDirectBookSameWindow(config)
        ? '_self'
        : '_blank';
      window.open(viewMoreBookOnlineUrl, target);
    }
  };

  const handleCloseModalPanel = () => {
    closeAppDrawer();
    setShowProviderModal(undefined);
    setSelectedDate(undefined);
  };

  const isCernerEmbedded = isCCF();

  const cardType = getConfigProperty(config, 'provider_tile.type', 'classic');
  const ProviderTile =
    cardType === 'modern' ? ProviderTileModern : ProviderTileClassic;

  const providerCards = providers.map((provider, index) => {
    // from this point on the data access paths will need to change
    const slotsAndAppointmentInfo = getProviderSlotsAndAppointmentInfo(
      provider,
      slots
    );
    const displayName = getProviderDisplayName(provider);
    const phone = getProviderPhoneNumber(provider);
    const legacyBookingOptionsButtonProps = {
      onClick: bookingCallbackFunction(provider),
      ariaControls: BOOKING_MODAL.ID,
      ariaHasPopup: BOOKING_MODAL.ROLE,
      ariaExpanded: showBookingModal
    };
    const ctaDrawerItems = isCtaDrawerEnabled
      ? getCtaListItemsToRender({ provider, config })
      : [];
    // Only controls the button when darkship_feature_flags.use_cta_drawer is true.
    const showBookingCtaButton = Boolean(ctaDrawerItems?.length);
    return (
      <ProviderTileWrapper
        id={provider.id}
        key={provider.id}
        data-provider-id={provider.id}
        onProviderHover={onProviderHover}
        onKeyboardFocus={onKeyboardFocus}
        onKeyboardBlur={onKeyboardBlur}
        provider={provider}
        isProviderListMapsEnabled={isProviderListMapsEnabled}
      >
        <ProviderTile
          key={appointmentInfo?.relationship}
          isCCF={isCernerEmbedded}
          applyV9SummaryData={true}
          provider={provider}
          customerCode={customerCode}
          productName={productName}
          providerLocations={
            showProviderLocationsInProviderTile ? provider.locations : []
          }
          bookOnlineUrl={getRelativeParameterizedBookingUrl({
            provider,
            config,
            location,
            searchSummary
          })}
          config={config}
          searchSummary={searchSummary}
          track={getLogWithProvider(log, provider)}
          getWaitTimeCallbackFunction={getWaitTimeCallbackFunction}
          bookingCallback={bookingCallbackFunction(provider)}
          reviewCallback={providerCallbackFunction(
            provider,
            '#profile-reviews',
            slots
          )}
          reviewUrl={getProviderUrl({
            provider,
            location,
            match,
            hash: '#profile-reviews'
          })}
          locationsUrl={getProviderUrl({
            provider,
            location,
            match,
            hash: '#profile-locations'
          })}
          locationsCallback={providerCallbackFunction(
            provider,
            '#profile-locations',
            slots
          )}
          summaryCallback={providerCallbackFunction(provider, '', slots)}
          summaryUrl={getProviderSummaryUrl(
            provider,
            '',
            slots,
            profileSummaryParams,
            location,
            match
          )}
          lazyLoadImg={index > 2}
          onClickShowAvailability={() => onClickShowAvailability(provider)}
          availabilityTilesMessages={{
            moreAvailabilitiesLinkText: (
              <FormattedMessage {...messages.moreAvailabilitiesLinkText} />
            ),
            showAvailabilitiesBtnText: (
              <FormattedMessage {...messages.showAvailabilitiesBtnText} />
            ),
            noAvailabilityMessage: (
              <FormattedMessage
                {...messages.noAvailabilityMessage}
                values={{ displayName }}
              />
            ),
            availabilityErrorMsg: (
              <FormattedMessage
                {...messages.availabilityErrorMsg}
                values={{ displayName }}
              />
            ),
            availabilityNoMoreRetriesMsg: (
              <FormattedMessage
                {...messages.availabilityNoMoreRetriesMsg}
                values={{
                  displayName,
                  phoneNumber: phone
                }}
              />
            ),
            availabilityTryAgainBtnText: (
              <FormattedMessage {...messages.availabilityTryAgainBtnText} />
            ),
            showAvailabilitiesBtnAriaLabel: intl.formatMessage(
              messages.showAvailabilitiesBtnAriaLabelText,
              { providerName: displayName }
            ),
            slotsAriaLabel: (date, time, locationName) =>
              intl.formatMessage(messages.providerAvailabilitySlotsAriaLabel, {
                date,
                time,
                providerName: displayName,
                locationName
              }),
            viewMoreAriaLabel: intl.formatMessage(
              messages.providerAvailabilityViewMoreAriaLabel,
              { providerName: displayName }
            )
          }}
          availabilityLinkTarget={availabilityLinkTarget}
          maxAvailabilitiesToDisplay={MAX_AVAILABILITIES_TO_DISPLAY}
          onTimeSlotsClick={(event, data) =>
            handleTimeSlotsClick(event, TILE_CLICK_ORIGIN.PROVIDER_TILE, {
              ...data,
              provider
            })
          }
          onViewMoreClick={(event, data) => {
            // modern card requires the booking drawer
            if (cardType === 'modern' && isCtaDrawerEnabled) {
              const book = bookingCallbackFunction(provider);
              book(event);
            } else {
              handleViewMoreClick(event, TILE_CLICK_ORIGIN.PROVIDER_TILE, data);
            }
          }}
          onAvailabilityTilesLoaded={onAvailabilityTilesLoaded}
          getLocationUrl={(location) =>
            buildLocationURL(
              isLocationsModuleEnabled,
              location,
              searchSummary.query_string
            )
          }
          locationCallback={locationCallbackFunction}
          apptInfo={slotsAndAppointmentInfo?.apptInfo}
          slots={slotsAndAppointmentInfo?.slots}
          slotsStatus={getProviderSlotsStatus(provider, slotsStatus)}
          locationCTA={
            isModuleEnabled(config, MODULES.REFERRAL)
              ? (props) => (
                  <ProviderReferralButton
                    referrals={connector.state.context.referrals}
                    onAddReferral={connector.addReferral}
                    onRemoveReferral={connector.removeReferral}
                    canRefer={connector.canRefer(connector.state)}
                    hasMaxReferrals={connector.hasMaxReferrals(connector.state)}
                    referableProvider={{ ...props, config: config }}
                  />
                )
              : null
          }
          placementType={PLACEMENT_TYPE_SEARCH}
          bookingWarning={
            crmEnabled && (
              <FormattedMessage {...crmMessages.invalidPatientDataBooking} />
            )
          }
          hasBookingWarning={
            isModuleEnabled(config, MODULES.DIRECT_BOOK) &&
            crmEnabled &&
            connector.hasPatientValidationErrorsForBooking()
          }
          isBookingDisabled={crmEnabled && !connector.canBook(connector.state)}
          isProfileDisabled={!isModuleEnabled(config, MODULES.PROFILE)}
          slotsAriaProps={{
            expanded: showAvailabilityModal,
            controls: AVAILABILITY_DRAWER.ID,
            hasPopup: AVAILABILITY_DRAWER.ROLE
          }}
          bookingOptionsButton={
            isCtaDrawerEnabled
              ? {
                  onClick: bookingCallbackFunction(provider),
                  ariaControls: APP_DRAWER_IDS.CTA_DRAWER,
                  ariaHasPopup: 'dialog',
                  ariaExpanded: activeAppDrawer === APP_DRAWER_IDS.CTA_DRAWER
                }
              : legacyBookingOptionsButtonProps
          }
          showBookingCtaButton={showBookingCtaButton}
          initialActiveTab={appointmentInfo?.relationship ?? 'new'}
        />
      </ProviderTileWrapper>
    );
  });

  // when provider_tile.provider_card_tabs is true, the view mode will be list
  // provider card tabs are not supported with the grid view
  const cardTabsEnabled = getConfigProperty(
    config,
    'provider_tile.provider_card_tabs.is_enabled',
    false
  );
  const listDefaultView = getConfigProperty(
    config,
    'provider_list_default_view'
  );
  let viewMode = cardTabsEnabled ? LIST_VIEW_MODE.LIST : listDefaultView;
  if (cardType === 'modern') {
    viewMode = LIST_VIEW_MODE.MODERN_GRID;
  }

  return (
    <ProviderCardListContainer>
      <ProviderCardList
        id="provider-list"
        data-testid="ProviderList"
        className="result-column"
        mode={viewMode}
      >
        {providerCards}
        {isModuleEnabled(config, MODULES.REFERRAL) && (
          <ProviderReferralList
            referrals={connector.state.context.referrals}
            maxReferralCount={connector.state.context.maxReferralCount}
            canRefer={connector.canRefer(connector.state)}
            hasReferrals={connector.hasReferrals(connector.state)}
            isReferDisabled={connector.isReferDisabled(connector.state)}
            showSpinner={connector.showSpinner(connector.state)}
            onRemoveReferral={connector.removeReferral}
            onSendReferralData={(referralMetaData) => {
              log('user_action.provider_detail.refer_provider', {
                referrals: connector.state.context.referrals.map((ref) => ({
                  provider_id: ref.provider.id,
                  location_id: ref.location.id
                }))
              });
              connector.sendReferralData(referralMetaData);
            }}
            referralMetaData={{ searchSummary }}
          />
        )}
        {showBookingModal && !isCtaDrawerEnabled && (
          <BookingModal
            config={config}
            provider={showProviderModal?.provider}
            log={getLogWithProvider}
            apptOptions={apptOptions}
            location={location}
            onClose={handleCloseModalPanel}
            searchSummary={searchSummary}
          />
        )}
        {isCtaDrawerEnabled && showBookingModal && (
          <CtaDrawer
            onClose={handleCloseModalPanel}
            log={log}
            provider={showProviderModal?.provider}
            config={config}
            searchSummary={searchSummary}
            apptOptions={apptOptions}
          />
        )}
        {showAvailabilityModal && (
          <AvailabilityDrawer
            location={location}
            config={config}
            provider={showProviderModal?.provider}
            onClose={handleCloseModalPanel}
            log={getLogWithProvider}
            appointmentInfo={
              selectedPatientRelationship
                ? {
                    ...appointmentInfo,
                    relationship: selectedPatientRelationship
                  }
                : appointmentInfo
            }
            onViewMoreClick={(event, data) =>
              handleViewMoreClick(
                event,
                TILE_CLICK_ORIGIN.AVAILABILITY_DRAWER,
                data
              )
            }
            fetchProviderAvailability={fetchProviderAvailability}
            selectedDate={selectedDate}
            bookOnlineUrl={getRelativeParameterizedBookingUrl({
              provider: showProviderModal?.provider,
              config,
              location,
              searchSummary
            })}
            onTimeSlotsClick={(event, data) =>
              handleTimeSlotsClick(
                event,
                TILE_CLICK_ORIGIN.AVAILABILITY_DRAWER,
                data
              )
            }
            fetchPurposesByProviderId={fetchPurposesByProviderId}
            purposeOptionsByProviderId={purposeOptionsByProviderId}
            availabilityError={availabilityError}
            purposesLoading={purposesLoading}
          />
        )}
      </ProviderCardList>
    </ProviderCardListContainer>
  );
}

export default withRouter(injectIntl(ProviderList));
